import React from 'react';
import styled from 'styled-components';
import { VariablePill } from './VariablePill';
import { clickablePillCss } from './clickablePillCss';

const Styled = styled(VariablePill)<{ $clickable?: boolean; $unpopulated?: boolean; $bordered?: boolean }>`
  ${({ $clickable, $unpopulated, $bordered }) => $clickable && clickablePillCss($unpopulated, $bordered)}
`;

interface ClickableVariablePillProps extends React.ComponentProps<typeof VariablePill> {
  /** Opt-in: only then does the pill get the hover/cursor affordance and click wiring. */
  clickable?: boolean;
  /** Click / Enter / Space. Receives the pill element (popover anchor). */
  onPillClick?: (el: HTMLElement) => void;
}

/**
 * THE actionable variable pill — the single component behind every clickable pill surface
 * (scenario-screen capsule, scenario-form Tiptap chip, AI-wizard formula pills). Owns the
 * shared hover/cursor treatment (clickablePillCss) and the button semantics (role, tabIndex,
 * click, Enter/Space), so all surfaces look and behave identically; surfaces only layer
 * layout css via `styled(ClickableVariablePill)`. Click stops propagation — a pill click
 * must never fall through to its host (e.g. the form's editor refocus).
 */
export const ClickableVariablePill: React.FC<ClickableVariablePillProps> = ({
  clickable,
  onPillClick,
  unpopulated,
  bordered = true,
  children,
  ...rest
}) => {
  const wired = !!clickable && !!onPillClick;
  return (
    <Styled
      unpopulated={unpopulated}
      bordered={bordered}
      $clickable={wired}
      $unpopulated={unpopulated}
      $bordered={bordered}
      role={wired ? 'button' : undefined}
      tabIndex={wired ? 0 : undefined}
      onClick={
        wired
          ? (e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              onPillClick!(e.currentTarget);
            }
          : undefined
      }
      onKeyDown={
        wired
          ? (e: React.KeyboardEvent<HTMLElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPillClick!(e.currentTarget as HTMLElement);
              }
            }
          : undefined
      }
      {...rest}
    >
      {children}
    </Styled>
  );
};
